/**
 * React hook for browser-extension pairing, window message listeners, and connection state.
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useCallback, useEffect, useRef, useState } from "react";
import { ToastMessages } from "../components/toasts/toastMessages";
import {
  notifyExtensionLogOut,
  requestExtensionAuthState,
  sendPairingCodeToExtension,
} from "../services/extensionMessaging";
import { createPairingCode } from "../services/pairingCodeService";
import {
  RISA_EXTENSION_SOURCE,
  type ExtensionAuthStatePayload,
  type ExtensionMessage,
} from "../settings/profile/types";
import { logError } from "../utils/customLogger";

/**
 * Manages browser-extension pairing, message listeners, and connection state.
 *
 * @param enabled - When `false`, no extension listeners are registered and no
 *   auth-state request is sent (used when the facility disables the feature)
 * @returns Extension connection state and handlers for the pairing UI
 */
export function useExtensionConnection(enabled = true) {
  const [deviceId, setDeviceId] = useState("");
  const [pairingCode, setPairingCode] = useState("");
  const [connectingExtension, setConnectingExtension] = useState(false);
  const [extensionConnected, setExtensionConnected] = useState(false);
  const [deviceIdInputKey, setDeviceIdInputKey] = useState(0);
  const extensionMessageHandlerRef = useRef<
    ((event: MessageEvent) => void) | null
  >(null);

  /**
   * Remove the active RISA extension `message` listener, if one is registered.
   */
  const detachExtensionListener = useCallback(() => {
    if (extensionMessageHandlerRef.current) {
      window.removeEventListener("message", extensionMessageHandlerRef.current);
      extensionMessageHandlerRef.current = null;
    }
  }, []);

  /**
   * Register a `window` `message` listener for RISA extension login/logout events.
   *
   * On `LOGGED_IN` the connection UI flips to the connected state; on `LOGGED_OUT`
   * it resets to the default state and tears the listener down. Any previously
   * attached listener is removed first so the listener is never duplicated.
   */
  const attachExtensionListener = useCallback(() => {
    detachExtensionListener();

    const handleExtensionMessage = (event: MessageEvent) => {
      console.info("[RISA_EXTENSION] window message received:", event);
      if (event.source !== window) return;

      const data = event.data as ExtensionMessage | undefined;
      if (data?.source !== RISA_EXTENSION_SOURCE) return;

      console.info("[RISA_EXTENSION] extension event matched:", data);

      if (data.type === "LOGGED_IN") {
        setExtensionConnected(true);
        setPairingCode("");
        toast.success(ToastMessages.EXTENSION_LOGGED_IN);
      } else if (data.type === "LOGGED_OUT") {
        setExtensionConnected(false);
        setPairingCode("");
        toast.success(ToastMessages.EXTENSION_LOGGED_OUT);
        detachExtensionListener();
      } else if (data.type === "AUTH_STATE") {
        const { deviceId: reportedDeviceId, loggedIn } =
          (data.payload as ExtensionAuthStatePayload | undefined) ?? {
            deviceId: null,
            loggedIn: false,
          };
        setExtensionConnected(loggedIn);
        if (reportedDeviceId) {
          setDeviceId(reportedDeviceId);
          setDeviceIdInputKey((key) => key + 1);
        }
      }
    };

    extensionMessageHandlerRef.current = handleExtensionMessage;
    window.addEventListener("message", handleExtensionMessage);
  }, [detachExtensionListener]);

  useEffect(() => {
    if (!enabled) return;

    attachExtensionListener();
    requestExtensionAuthState();
    return () => detachExtensionListener();
  }, [enabled, attachExtensionListener, detachExtensionListener]);

  /**
   * Reset the connected UI and ask the extension to log out.
   *
   * Clears the local connected state (the "Connected" label disappears) and
   * posts a `LOG_OUT` message on the window so the RISA extension can tear down
   * its own session.
   */
  const handleDisconnectExtension = useCallback(() => {
    setExtensionConnected(false);
    setPairingCode("");
    notifyExtensionLogOut();
  }, []);

  /**
   * Copy a pairing code to the clipboard, optionally toasting the result.
   *
   * @param code - The pairing code to write to the clipboard
   * @param silent - When `true`, suppress the success/failure toast (used by the
   *   connect flow so only a single connection toast is shown)
   * @returns `true` when the copy succeeded, otherwise `false`
   */
  const copyPairingCodeToClipboard = async (
    code: string,
    silent = false,
  ): Promise<boolean> => {
    try {
      await navigator.clipboard.writeText(code);
      if (!silent) toast.success(ToastMessages.PAIRING_CODE_COPIED);
      return true;
    } catch (error) {
      logError(error as Error, "Error copying pairing code");
      if (!silent) toast.error(ToastMessages.PAIRING_CODE_COPY_FAILED);
      return false;
    }
  };

  /**
   * Generates a pairing code for the entered device id and hands it to the extension.
   *
   * On success the code is pushed straight to the RISA extension via a
   * `PAIRING_CODE` window message so it can auto-redeem and open its logged-in
   * state without a manual copy-paste. The code is still displayed (and copied to
   * the clipboard) as a fallback for when the extension is not on the page.
   *
   * @returns Resolves when the pairing-code request completes
   */
  const handleConnectExtension = async () => {
    attachExtensionListener();
    setConnectingExtension(true);
    try {
      const trimmedDeviceId = deviceId.trim();
      const response = await createPairingCode({ device_id: trimmedDeviceId });

      if (response.success && response.code) {
        setPairingCode(response.code);
        sendPairingCodeToExtension(response.code, trimmedDeviceId);
        // Copy silently and skip the intermediate "pairing code generated" toast:
        // the single connection confirmation is emitted on the LOGGED_IN message.
        await copyPairingCodeToClipboard(response.code, true);
      } else {
        toast.error(response.message || ToastMessages.EXTENSION_CONNECT_ERROR);
      }
    } catch (error) {
      logError(error as Error, "Error connecting to extension");
      toast.error(ToastMessages.EXTENSION_CONNECT_ERROR);
    } finally {
      setConnectingExtension(false);
    }
  };

  /** Copies the currently displayed pairing code to the clipboard. */
  const handleCopyPairingCode = () => copyPairingCodeToClipboard(pairingCode);

  return {
    deviceId,
    pairingCode,
    connectingExtension,
    extensionConnected,
    deviceIdInputKey,
    setDeviceId,
    handleDisconnectExtension,
    handleConnectExtension,
    handleCopyPairingCode,
  };
}
